import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { TrophyIcon, FlameIcon, StarIcon, TrendingUpIcon, CalendarIcon, BarChart2Icon } from 'lucide-react';
import { Tooltip } from './ui/Tooltip';
interface ProgressStatsProps {
  progress: number;
}
export function ProgressStats({
  progress
}: ProgressStatsProps) {
  const {
    isArabic
  } = useLanguage();
  const [showTrends, setShowTrends] = useState(false);
  const weeklyProgress = [30, 35, 42, 45, 50, 55, 60];
  const daysActive = [true, true, true, false, true, true, true];
  const toggleTrends = () => {
    setShowTrends(!showTrends);
  };
  return <div className="space-y-6" data-id="element-1207">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1208">
        {/* Overall Progress */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 hover:shadow-lg transition-all cursor-pointer" onClick={toggleTrends} data-id="element-1209">
          <div className="flex items-start justify-between" data-id="element-1210">
            <div data-id="element-1211">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1" data-id="element-1212">
                {isArabic ? 'التقدم الكلي' : 'Overall Progress'}
              </p>
              <h4 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-1213">
                {progress}%
              </h4>
            </div>
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg" data-id="element-1214">
              <TrophyIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-1215" />
            </div>
          </div>
          <div className="mt-4 h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-1216">
            <div className="h-full bg-blue-600 dark:bg-blue-400 transition-all duration-300" style={{
            width: `${progress}%`
          }} data-id="element-1217" />
          </div>
          {progress >= 50 && <div className="mt-3 flex items-center gap-1 text-xs text-green-600 dark:text-green-400" data-id="element-1218">
              <TrendingUpIcon className="w-3.5 h-3.5" data-id="element-1219" />
              <span data-id="element-1220">{isArabic ? 'تقدم جيد!' : 'Good progress!'}</span>
            </div>}
        </div>
        {/* Current Streak */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 hover:shadow-lg transition-all" data-id="element-1221">
          <div className="flex items-start justify-between" data-id="element-1222">
            <div data-id="element-1223">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1" data-id="element-1224">
                {isArabic ? 'التتابع الحالي' : 'Current Streak'}
              </p>
              <h4 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-1225">
                7 {isArabic ? 'أيام' : 'days'}
              </h4>
            </div>
            <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg" data-id="element-1226">
              <FlameIcon className="w-5 h-5 text-orange-600 dark:text-orange-400" data-id="element-1227" />
            </div>
          </div>
          {/* Week calendar */}
          <div className="mt-4 flex justify-between" data-id="element-1228">
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => <Tooltip key={index} content={daysActive[index] ? isArabic ? 'نشط' : 'Active' : isArabic ? 'غير نشط' : 'Inactive'} data-id="element-1229">
                <div className="flex flex-col items-center" data-id="element-1230">
                  <span className="text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-1231">
                    {day}
                  </span>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${daysActive[index] ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'}`} data-id="element-1232">
                    {daysActive[index] ? <FlameIcon className="w-3.5 h-3.5" data-id="element-1233" /> : <span className="w-1.5 h-1.5 rounded-full bg-gray-400 dark:bg-gray-500" data-id="element-1234" />}
                  </div>
                </div>
              </Tooltip>)}
          </div>
        </div>
        {/* XP Points */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 hover:shadow-lg transition-all" data-id="element-1235">
          <div className="flex items-start justify-between" data-id="element-1236">
            <div data-id="element-1237">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1" data-id="element-1238">
                {isArabic ? 'نقاط الخبرة' : 'XP Points'}
              </p>
              <h4 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-1239">
                1,250 XP
              </h4>
            </div>
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg" data-id="element-1240">
              <StarIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400" data-id="element-1241" />
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between" data-id="element-1242">
            <div className="flex items-center gap-1.5" data-id="element-1243">
              <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1244">
                {isArabic ? 'المستوى' : 'Level'}
              </span>
              <span className="px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400 rounded-full text-xs font-medium" data-id="element-1245">
                5
              </span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1246">
              {isArabic ? '750 نقطة للمستوى التالي' : '750 XP to next level'}
            </div>
          </div>
          <div className="mt-2 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-1247">
            <div className="h-full bg-yellow-500 dark:bg-yellow-400 transition-all duration-300" style={{
            width: '62%'
          }} data-id="element-1248" />
          </div>
        </div>
      </div>
      {/* Trends panel (expandable) */}
      {showTrends && <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 animate-slideDown" data-id="element-1249">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2" data-id="element-1250">
            <BarChart2Icon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-1251" />
            {isArabic ? 'تقدمك الأسبوعي' : 'Your Weekly Progress'}
          </h3>
          <div className="flex items-end justify-between h-40 mb-2" data-id="element-1252">
            {weeklyProgress.map((day, index) => <div key={index} className="flex flex-col items-center w-1/7" data-id="element-1253">
                <div className="w-10 bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-400 rounded-t-sm transition-all" style={{
            height: `${day * 0.8}%`
          }} data-id="element-1254">
                  <div className="opacity-0 hover:opacity-100 transition-opacity absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded-md whitespace-nowrap" data-id="element-1255">
                    {day}%
                  </div>
                </div>
              </div>)}
          </div>
          <div className="flex justify-between" data-id="element-1256">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => <div key={index} className="text-xs text-gray-500 dark:text-gray-400 text-center w-1/7" data-id="element-1257">
                  {isArabic ? day.substring(0, 1) : day.substring(0, 1)}
                </div>)}
          </div>
        </div>}
    </div>;
}